import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PanelFrame } from '../workspace/PanelFrame';
import { MessagesSquare, Sparkles, Send, Paperclip, Wand2, Download, Trash2, Loader2, Lock } from 'lucide-react';
import { usePW } from '@/lib/store';
import { useAuth } from '@/lib/auth_context';
import { useDemo } from '@/lib/demo_context';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { exportApi } from '@/lib/api';
import { toast } from 'sonner';
import { ChatModelPicker } from './ChatModelPicker';
import { consumePendingChatPrompt } from '@/lib/pendingIntents';   // Feb-2026
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';

const QUICK = [
  'Tighten the dialogue in chapter 6',
  'Suggest a stronger hook for Chapter 1',
  'Audit voice consistency for my antagonist',
  'Cut 800 words from the act-2 lull',
];

export function ClaudeChatPanel({ onClose, maximized, onMaximize, simpleMode }) {
  const { chat, sendChat, clearChat, chatBusy, streamingChatText, project } = usePW();
  const { user } = useAuth();
  const { isDemo, recordLock } = useDemo();
  // Default-deny while permissions are still loading so we don't briefly
  // flash enabled chat controls to a contributor.
  const canGenerate = Boolean(user?.permissions?.generate_ai);
  const gateMsg = isDemo
    ? 'AI chat is locked in demo mode — sign up free to unlock.'
    : 'AI chat is disabled for your role. Ask an admin to upgrade to Editor.';
  const [text, setText] = useState('');
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat, streamingChatText]);

  // Feb-2026 · Task Launcher's "Get Feedback" card seeds a canned prompt
  // into this panel rather than duplicating the chat-send logic. Also
  // check for a pending prompt set BEFORE this panel mounted (the
  // launcher switches the chat panel on in the same click that fires
  // the event, so the panel's own listener can't be attached in time).
  useEffect(() => {
    const pending = consumePendingChatPrompt();
    if (pending) setText(pending);
    const onSeed = (e) => {
      // Clear any leftover pending flag so a later remount doesn't
      // re-fill the input with a prompt nobody asked for this time.
      consumePendingChatPrompt();
      setText(e.detail?.prompt || '');
    };
    window.addEventListener('loom:chat-seed-prompt', onSeed);
    return () => window.removeEventListener('loom:chat-seed-prompt', onSeed);
  }, []);

  const submit = () => {
    if (isDemo) { recordLock('AI chat'); return; }
    if (!canGenerate) { toast.warning(gateMsg); return; }
    if (!text.trim() || chatBusy) return;
    sendChat(text.trim());
    setText('');
  };

  // iter-43 — also gate the QUICK prompt buttons so they trip the demo lock.
  const sendQuick = (q) => {
    if (isDemo) { recordLock('AI chat'); return; }
    if (!canGenerate) { toast.warning(gateMsg); return; }
    sendChat(q);
  };

  const saveConversation = async (fmt) => {
    if (!project) return;
    try {
      await exportApi.chat(project.id, fmt);
      toast.success(`Conversation saved as ${fmt.toUpperCase()}.`);
    } catch { toast.error('Could not export conversation.'); }
  };

  const onClear = async () => {
    await clearChat();
    toast.success('Conversation cleared.');
  };

  // Accept-on-Highlight: user selects text in Claude's reply → floating button
  // copies it to clipboard so they can paste into their manuscript.
  const handleAcceptFromClaude = useCallback((selectedText) => {
    if (!selectedText) return;
    try {
      navigator.clipboard.writeText(selectedText).then(() => {
        toast.success('Copied — paste into your manuscript with ⌘V', { duration: 3500 });
      }).catch(() => {
        toast.info('Selected text ready — use ⌘C to copy');
      });
    } catch {
      toast.info('Selected text ready — use ⌘C to copy');
    }
  }, []);

  return (
    <PanelFrame
      title="Chat with AI"
      icon={MessagesSquare}
      onClose={onClose}
      onMaximize={onMaximize}
      maximized={maximized}
      testId="chat-panel"
      actions={
        <div className="flex items-center gap-0.5">
          <span className="flex items-center gap-1.5 px-1.5 text-[13px] text-success" data-testid="chat-connection-status">
            <span className={cn('h-1.5 w-1.5 rounded-full', chatBusy ? 'bg-warning anim-pulse-soft' : 'bg-success')} />
            {chatBusy ? 'Thinking…' : 'Online'}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button title="Save conversation" data-testid="chat-save-menu-trigger" className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary hover:text-foreground">
                <Download className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="text-[13.5px]">
              <DropdownMenuItem data-testid="chat-save-pdf" onClick={() => saveConversation('pdf')}>Save as PDF</DropdownMenuItem>
              <DropdownMenuItem data-testid="chat-save-docx" onClick={() => saveConversation('docx')}>Save as Word (.docx)</DropdownMenuItem>
              <DropdownMenuItem data-testid="chat-save-txt" onClick={() => saveConversation('txt')}>Save as Plain Text</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button onClick={onClear} title="Clear conversation" data-testid="chat-clear-button" className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary hover:text-destructive">
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      }
    >
      <div className="flex h-full flex-col">
        {project && !simpleMode && <ChatModelPicker projectId={project.id} disabled={chatBusy || !canGenerate || isDemo} />}
        <div className="flex-1 overflow-auto scroll-thin px-3 py-3 space-y-3">
          {chat.map((m) => (
            <Message key={m.id} from={m.from} text={m.text} onAccept={handleAcceptFromClaude} />
          ))}
          {chatBusy && streamingChatText && (
            <Message from="claude" text={streamingChatText} streaming />
          )}
          {chatBusy && !streamingChatText && (
            <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> The AI is composing a reply…
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="shrink-0 border-t-2 border-primary/40 bg-zinc-900/60 px-3 pt-3 pb-2">
          {!canGenerate && (
            <div
              data-testid="chat-rbac-banner"
              className="mb-2 flex items-center gap-2 rounded-sm border border-warning/40 bg-warning/10 px-2 py-1.5 text-[13px] text-warning-foreground"
            >
              <Lock className="h-3.5 w-3.5 text-warning" />
              <span>{gateMsg}</span>
            </div>
          )}
          {isDemo && canGenerate && (
            <div
              data-testid="chat-demo-banner"
              className="mb-2 flex items-center gap-2 rounded-sm border border-primary/40 bg-primary/10 px-2 py-1.5 text-[13px]"
            >
              <Lock className="h-3.5 w-3.5 text-primary" />
              <span><b>Demo mode</b> — AI is locked. Sign up free to unlock real generations.</span>
            </div>
          )}

          {/* Quick-prompt chips */}
          <div className="mb-2 flex flex-wrap gap-1">
            {QUICK.map(q => (
              <button
                key={q}
                onClick={() => sendQuick(q)}
                disabled={!canGenerate || chatBusy}
                title={canGenerate ? (isDemo ? 'Locked in demo mode' : '') : gateMsg}
                data-testid={`chat-quick-${q.slice(0, 12).replace(/\s+/g, '-').toLowerCase()}`}
                className="rounded-full border border-primary/25 bg-primary/8 px-2.5 py-1 text-[12px] text-primary/80 hover:border-primary/60 hover:bg-primary/15 hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Sparkles className="mr-1 inline h-3 w-3" />{q}
              </button>
            ))}
          </div>

          {/* ── Main input box — parchment background, amber-glow border ───── */}
          <div
            className={cn(
              'relative rounded-lg border-2 border-primary/50 bg-[hsl(var(--canvas-paper))] shadow-[0_0_0_3px_hsl(var(--primary)/0.12)] transition-shadow focus-within:border-primary focus-within:shadow-[0_0_0_4px_hsl(var(--primary)/0.22)]',
              !canGenerate && 'opacity-60',
            )}
            data-testid="chat-input-container"
          >
            <div className="flex items-start gap-1.5 p-2">
              <button
                data-testid="chat-attachment-button"
                title="Attach reference"
                className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[hsl(var(--canvas-paper-text)/0.4)] hover:bg-[hsl(var(--canvas-paper-text)/0.08)] hover:text-primary transition-colors disabled:opacity-50"
                disabled={!canGenerate}
              >
                <Paperclip className="h-3.5 w-3.5" />
              </button>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
                placeholder={canGenerate ? 'Ask the AI to tighten, expand, or analyze…' : 'AI chat is disabled for your role.'}
                rows={2}
                disabled={!canGenerate}
                data-testid="chat-input"
                className="min-h-[44px] flex-1 resize-none border-0 bg-transparent p-1 text-[14px] text-[hsl(var(--canvas-paper-text))] placeholder:text-[hsl(var(--canvas-paper-text)/0.35)] shadow-none focus-visible:ring-0 disabled:cursor-not-allowed"
              />
            </div>
            {/* Bottom row: hint + send */}
            <div className="flex items-center justify-between border-t border-[hsl(var(--canvas-paper-text)/0.12)] px-3 py-1.5">
              <span className="text-[11px] text-[hsl(var(--canvas-paper-text)/0.35)]">↵ Send · Shift+↵ newline</span>
              <Button
                size="sm"
                onClick={submit}
                disabled={chatBusy || !text.trim() || (!canGenerate && !isDemo)}
                title={canGenerate ? (isDemo ? 'Locked in demo mode' : '') : gateMsg}
                data-testid="chat-send-btn"
                className="h-7 gap-1.5 bg-primary px-3 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {(!canGenerate || isDemo) ? <Lock className="h-3.5 w-3.5" /> : chatBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {chatBusy ? 'Thinking…' : 'Send'}
              </Button>
            </div>
          </div>

          {/* Footer meta */}
          <div className="mt-1.5 flex items-center gap-1 text-[11px] text-zinc-600">
            <Sparkles className="h-2.5 w-2.5 text-primary/50" />
            <span>Reference memory: <b className="text-zinc-500">Full novel</b> · Variation count: 3</span>
          </div>
        </div>
      </div>
    </PanelFrame>
  );
}

function Message({ from, text, streaming = false, onAccept }) {
  const isUser = from === 'user';
  const [bubble, setBubble]   = useState(null); // {x, y, selectedText}
  const msgRef                = useRef(null);

  // Detect mouse-up selection inside this Claude message
  const handleMouseUp = () => {
    if (isUser || streaming) return;
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.rangeCount) { setBubble(null); return; }
    const selected = sel.toString().trim();
    if (!selected) { setBubble(null); return; }
    // Only act on selection inside this bubble
    const range = sel.getRangeAt(0);
    if (!msgRef.current?.contains(range.commonAncestorContainer)) { setBubble(null); return; }
    const rect = range.getBoundingClientRect();
    setBubble({ x: rect.left + rect.width / 2, y: rect.top - 6, selectedText: selected });
  };

  // Clear the bubble if user clicks elsewhere
  useEffect(() => {
    const clear = (e) => {
      if (!msgRef.current?.contains(e.target)) setBubble(null);
    };
    document.addEventListener('mousedown', clear);
    return () => document.removeEventListener('mousedown', clear);
  }, []);

  return (
    <div className={cn('flex gap-2', isUser ? 'flex-row-reverse' : 'flex-row')} ref={msgRef}>
      <div className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-sm font-mono-stat text-[13px] font-semibold',
        isUser ? 'bg-secondary text-foreground' : 'bg-primary/15 text-primary')}>
        {isUser ? 'D' : <Wand2 className={cn('h-3 w-3', streaming && 'animate-pulse')} />}
      </div>
      <div
        className={cn(
          'relative max-w-[88%] rounded-md border px-2.5 py-2 text-[14px] leading-relaxed whitespace-pre-wrap select-text',
          isUser
            ? 'border-border bg-background text-foreground'
            : 'border-primary/20 bg-primary/5 text-foreground/95',
          streaming && 'ring-1 ring-primary/30',
        )}
        onMouseUp={handleMouseUp}
        data-testid={isUser ? 'chat-msg-user' : 'chat-msg-claude'}
      >
        {text}
        {streaming && <span className="caret-blink ml-0.5" />}

        {/* Accept-on-highlight floating button */}
        {bubble && !isUser && (
          <button
            type="button"
            data-testid="chat-accept-selection-btn"
            onMouseDown={(e) => {
              e.preventDefault(); // keep text selected
              onAccept?.(bubble.selectedText);
              setBubble(null);
            }}
            style={{ position: 'fixed', left: bubble.x, top: bubble.y, transform: 'translate(-50%, -100%)' }}
            className="z-50 flex items-center gap-1 rounded-full border border-primary/40 bg-primary px-2.5 py-1 text-[11px] font-bold text-primary-foreground shadow-lg shadow-primary/30 hover:bg-primary/90 transition-colors"
          >
            <Send className="h-2.5 w-2.5" /> Accept → Send
          </button>
        )}
      </div>
    </div>
  );
}
